import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ChannelordersService, ChannelOrder } from '../../services/channelorders.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';

@Component({
	selector: 'app-channelorders',
	templateUrl: './channelorders.component.html',
	styleUrls: ['./channelorders.component.scss']
})
export class ChannelordersComponent implements OnInit {
	color = 'blue';
	orders: ChannelOrder[] = [];
	total = 0;
	loading = false;
	pushingOrder: string | null = null;

	filters = {
		datefrom: this.defaultFrom(),
		dateto:   new Date().toISOString().split('T')[0],
		channel:  '',
		search:   '',
		limit:    100,
		offset:   0,
	};

	toastQueue: { msg: string; type: string }[] = [];

	displayedColumns = [
		'orderno','customer_name','orddate','order_channel','order_total',
		'll_push_status','distru_push_status','actions'
	];

	constructor(
		private location: Location,
		private svc: ChannelordersService,
		private globalSearchService: GlobalSearchService
	) {
		this.color = this.globalSearchService.getColor();
	}

	ngOnInit(): void { this.load(); }

	load(): void {
		this.loading = true;
		this.svc.getData(this.filters).subscribe({
			next: r => {
				this.orders = r.data || [];
				this.total  = r.total || 0;
				this.loading = false;
			},
			error: () => { this.loading = false; }
		});
	}

	push(orderno: string, channel: 'leaflink' | 'distru'): void {
		this.pushingOrder = orderno + ':' + channel;
		const obs = channel === 'leaflink'
			? this.svc.pushLeaflink(orderno)
			: this.svc.pushDistru(orderno);

		obs.subscribe({
			next: r => {
				this.pushingOrder = null;
				if (r.success) {
					this.showToast('Pushed to ' + this.channelLabel(channel) + (r.external_id ? ' — ' + r.external_id : ''), 'success');
					this.load();
				} else {
					this.showToast(r.message || 'Push failed', 'danger');
				}
			},
			error: () => {
				this.pushingOrder = null;
				this.showToast('Push failed', 'danger');
			}
		});
	}

	retry(orderno: string, channel: string): void {
		this.pushingOrder = orderno + ':' + channel;
		this.svc.retryPush(orderno, channel).subscribe({
			next: r => {
				this.pushingOrder = null;
				if (r.success) {
					this.showToast('Retry succeeded', 'success');
					this.load();
				} else {
					this.showToast(r.message || 'Retry failed', 'danger');
				}
			},
			error: () => { this.pushingOrder = null; }
		});
	}

	pollInbound(channel: 'leaflink' | 'distru'): void {
		this.svc.pollInbound(channel).subscribe({
			next: r => {
				this.showToast(
					this.channelLabel(channel) + ': ' + (r.queued ?? 0) + ' new orders queued', 'info'
				);
				this.load();
			}
		});
	}

	exportCsv(): void {
		if (!this.orders.length) return;
		const cols = this.displayedColumns.filter(c => c !== 'actions');
		const header = cols.join(',');
		const rows = this.orders.map(o =>
			cols.map(c => '"' + String((o as any)[c] ?? '').replace(/"/g, '""') + '"').join(',')
		);
		const csv = [header, ...rows].join('\n');
		const blob = new Blob([csv], { type: 'text/csv' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = 'channel-orders-' + this.filters.datefrom + '-' + this.filters.dateto + '.csv';
		a.click();
		URL.revokeObjectURL(url);
	}

	pushLabel(order: ChannelOrder, channel: 'leaflink' | 'distru'): string {
		const status = channel === 'leaflink' ? order.ll_push_status : order.distru_push_status;
		if (!status || status === 'pending') return 'Push to ' + this.channelLabel(channel);
		if (status === 'failed' || status === 'dead_letter') return 'Retry ' + this.channelLabel(channel);
		return 'Re-push ' + this.channelLabel(channel);
	}

	pushBadgeClass(status: string | undefined): string {
		if (!status) return 'badge bg-secondary';
		const map: Record<string, string> = {
			pushed:      'badge bg-success',
			pending:     'badge bg-warning text-dark',
			failed:      'badge bg-danger',
			dead_letter: 'badge bg-dark',
		};
		return map[status] || 'badge bg-secondary';
	}

	isPushing(orderno: string, channel: string): boolean {
		return this.pushingOrder === orderno + ':' + channel;
	}

	channelLabel(c: string): string {
		return c === 'leaflink' ? 'LeafLink' : 'Distru';
	}

	back(): void { this.location.back(); }

	private defaultFrom(): string {
		const d = new Date();
		d.setDate(d.getDate() - 30);
		return d.toISOString().split('T')[0];
	}

	private showToast(msg: string, type: string): void {
		this.toastQueue.push({ msg, type });
		setTimeout(() => this.toastQueue.shift(), 4000);
	}
}
